import { useMemo, useState } from "react"
import { DateTime } from "luxon"
import {
  UserPlus,
  MousePointerClick,
  Mail,
  MessageSquare,
  Brain
} from "lucide-react"
import { useUserRoles } from "../../common/hooks"
import {
  useFetchSubscriptionCountQuery,
  useFetchLeadCountQuery,
  useFetchSubscriptionEmailStatsQuery,
  useFetchSubscriptionEventCountByBrandQuery
} from "../../redux/queries/lead"
import {
  useFetchBillingConfigQuery,
  useFetchBillingPlansQuery
} from "../../redux/queries/billing-config"
import { SubscriptionEmailStatsType } from "../../types"
import { useGetAiTokenUsageQuery } from "../../redux/queries/advisor"
import MonthSelector from "./month-selector"
import UsageMeter from "./usage-meter"

import { BillingOverageMetric } from "../../types/billing-config"

type MetricKey = "subscriptions" | "leads" | "emails" | "sms" | "aiTokens"

const calcOverageCost = (
  overage: number,
  metric: BillingOverageMetric
): number => {
  if (overage <= 0) return 0
  let total = metric.base_fee ?? 0
  if (metric.tiers) {
    let remaining = overage
    for (const tier of metric.tiers) {
      if (remaining <= 0) break
      const tierSize = tier.stop != null ? tier.stop - tier.start : remaining
      const qty = Math.min(remaining, tierSize)
      const rate = tier.cpl ?? tier.cpe ?? tier.cpt ?? 0
      total += qty * rate
      remaining -= qty
    }
  } else if (metric.cpt != null) {
    // Flat rate per unit (e.g. $25 per 1M tokens)
    const unitSize = metric.unit === "1M tokens" ? 1_000_000 : 1
    total += (overage / unitSize) * metric.cpt
  }
  return total
}

const PLAN_BADGE_COLORS: Record<string, string> = {
  launch: "#3B82F6",
  core: "#63C7C4",
  growth: "#776ff6",
  custom: "#232227"
}

const PlatformUsage = () => {
  const { user, isBankAdmin, isSuperAdmin } = useUserRoles()
  const [selectedMonth, setSelectedMonth] = useState(() =>
    DateTime.now().startOf("month")
  )

  const orgId = useMemo(
    () => (!isSuperAdmin && isBankAdmin ? user?.org?.id : undefined),
    [isBankAdmin, isSuperAdmin, user?.org?.id]
  )

  const { range, startDate, endDate, since, until } = useMemo(() => {
    const now = DateTime.now()
    const start = selectedMonth.startOf("month")
    const end = selectedMonth.endOf("month")
    const effectiveEnd = end > now ? now : end
    return {
      range: Math.ceil(now.diff(start, "days").days),
      startDate: start.toFormat("yyyy-MM-dd"),
      endDate: effectiveEnd.toFormat("yyyy-MM-dd"),
      since: start.toISO()!,
      until: effectiveEnd.toISO()!
    }
  }, [selectedMonth])

  const dateParams = { range, start_date: startDate, end_date: endDate }

  // Billing config (bank admins only)
  const billingConfig = useFetchBillingConfigQuery(
    { org_id: orgId! },
    { skip: !orgId }
  )
  const billingPlans = useFetchBillingPlansQuery()
  const planLevel = billingConfig.data?.[0]?.details?.level
  const planDetails = planLevel
    ? billingPlans.data?.plans?.[planLevel]
    : undefined
  const tierLimits = useMemo(() => {
    if (!planDetails) return undefined
    return {
      leads: planDetails.included_leads,
      emails: planDetails.included_emails,
      sms: planDetails.included_sms_threads ?? 0
    } as Partial<Record<MetricKey, number>>
  }, [planDetails])
  const baseCost = planDetails?.base_monthly_price ?? 0

  // Subscriptions
  const subscriptions = useFetchSubscriptionCountQuery({
    ...dateParams,
    org_id: orgId
  })
  const subscriptionTotal = useMemo(
    () =>
      (subscriptions.data ?? []).reduce(
        (sum, row) => sum + row.subscription_count,
        0
      ),
    [subscriptions.data]
  )

  // Leads
  const leads = useFetchLeadCountQuery({ ...dateParams, org_id: orgId })
  const leadTotal = useMemo(
    () => (leads.data ?? []).reduce((sum, row) => sum + row.lead_count, 0),
    [leads.data]
  )

  // Emails (intro + rate alerts)
  const introEmails = useFetchSubscriptionEmailStatsQuery({
    ...dateParams,
    email_type: SubscriptionEmailStatsType.INTRO,
    org_id: orgId
  })
  const rateAlertEmails = useFetchSubscriptionEmailStatsQuery({
    ...dateParams,
    email_type: SubscriptionEmailStatsType.RATE_ALERT,
    org_id: orgId
  })
  const sumEmailSent = (data: { email_sent: number }[] | undefined) =>
    (data ?? []).reduce((s, r) => s + r.email_sent, 0)
  const emailTotal = useMemo(
    () => sumEmailSent(introEmails.data) + sumEmailSent(rateAlertEmails.data),
    [introEmails.data, rateAlertEmails.data]
  )
  const emailsLoading = introEmails.isLoading || rateAlertEmails.isLoading
  const emailsError = introEmails.isError && rateAlertEmails.isError

  // SMS sent
  const followupSms = useFetchSubscriptionEventCountByBrandQuery({
    ...dateParams,
    event_name: "followup_sms_sent",
    org_id: orgId
  })
  const leadFollowupSms = useFetchSubscriptionEventCountByBrandQuery({
    ...dateParams,
    event_name: "lead_followup_sms_sent",
    org_id: orgId
  })
  const smsTotal =
    (followupSms.data?.grandTotal ?? 0) +
    (leadFollowupSms.data?.grandTotal ?? 0)
  const smsLoading = followupSms.isLoading || leadFollowupSms.isLoading
  const smsError = followupSms.isError && leadFollowupSms.isError

  // AI tokens
  const aiTokens = useGetAiTokenUsageQuery({ since, until })

  // Overage calculations (tiered pricing from API)
  const overagePricing = billingPlans.data?.overage
  const overages = useMemo(() => {
    if (!tierLimits || !overagePricing) return null
    const items = {
      leads: overagePricing.leads
        ? calcOverageCost(
            Math.max(0, leadTotal - (tierLimits.leads ?? 0)),
            overagePricing.leads
          )
        : 0,
      emails: overagePricing.emails
        ? calcOverageCost(
            Math.max(0, emailTotal - (tierLimits.emails ?? 0)),
            overagePricing.emails
          )
        : 0,
      sms: overagePricing.sms
        ? calcOverageCost(
            Math.max(0, smsTotal - (tierLimits.sms ?? 0)),
            overagePricing.sms
          )
        : 0
    }
    const total = Object.values(items).reduce((s, v) => s + v, 0)
    return { ...items, total }
  }, [tierLimits, overagePricing, leadTotal, emailTotal, smsTotal])

  // AI token overage (shown on meter but not included in billing total)
  const aiTokenTotal = aiTokens.data?.totalTokens ?? 0
  const aiTokenOverageCost = useMemo(() => {
    if (!overagePricing?.ai_advisor_tokens) return undefined
    return calcOverageCost(aiTokenTotal, overagePricing.ai_advisor_tokens)
  }, [overagePricing, aiTokenTotal])

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-lg font-medium text-base-dark">Usage</h2>
        <MonthSelector
          selectedMonth={selectedMonth}
          onChange={setSelectedMonth}
        />
      </div>

      {/* Plan banner */}
      {planLevel && (
        <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 mb-2">
          <div className="flex items-center gap-3">
            <span
              className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full text-white"
              style={{
                backgroundColor: PLAN_BADGE_COLORS[planLevel] ?? "#232227"
              }}
            >
              {planLevel}
            </span>
            <span className="text-sm text-gray-500">
              {selectedMonth.toFormat("MMMM yyyy")} billing period
            </span>
          </div>
          {overages && (
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-gray-400">Est. charges</span>
              <span
                className="text-sm font-extrabold text-base-dark"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                ${(baseCost + overages.total).toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Usage meters */}
      <div className="divide-y divide-gray-100">
        <UsageMeter
          label="Subscriptions"
          icon={UserPlus}
          value={subscriptionTotal}
          isLoading={subscriptions.isLoading}
          isError={subscriptions.isError}
          index={0}
        />
        <UsageMeter
          label="New Leads"
          icon={MousePointerClick}
          value={leadTotal}
          included={tierLimits?.leads}
          overageCost={overages?.leads}
          isLoading={leads.isLoading}
          isError={leads.isError}
          index={1}
        />
        <UsageMeter
          label="Emails Sent"
          icon={Mail}
          value={emailTotal}
          included={tierLimits?.emails}
          overageCost={overages?.emails}
          isLoading={emailsLoading}
          isError={emailsError}
          index={2}
        />
        <UsageMeter
          label="SMS Sent"
          icon={MessageSquare}
          value={smsTotal}
          included={tierLimits?.sms}
          overageCost={overages?.sms}
          isLoading={smsLoading}
          isError={smsError}
          index={3}
        />
        <UsageMeter
          label="AI Tokens"
          icon={Brain}
          value={aiTokenTotal}
          overageCost={aiTokenOverageCost}
          isLoading={aiTokens.isLoading}
          isError={aiTokens.isError}
          compact
          index={4}
        />
      </div>

      {/* Billing summary */}
      {overages && planLevel && (
        <div className="mt-2 pt-5 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-gray-500">
              Base plan (
              {planLevel.charAt(0).toUpperCase() + planLevel.slice(1)})
            </span>
            <span className="text-gray-600">${baseCost.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between text-sm mb-3">
            <span className="text-gray-500">Overages</span>
            <span
              className={
                overages.total > 0
                  ? "text-red-400 font-medium"
                  : "text-gray-600"
              }
            >
              ${overages.total.toFixed(2)}
            </span>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <span className="text-sm font-medium text-gray-700">
              Estimated total
            </span>
            <span
              className="font-extrabold text-base-dark"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              ${(baseCost + overages.total).toFixed(2)}
              {planLevel === "custom" && "*"}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            {planLevel === "custom" && (
              <>*Custom plans are not subject to overages. </>
            )}
            Does not include add-ons or custom fees.
          </p>
        </div>
      )}
    </div>
  )
}

export default PlatformUsage
